import React from "react";
import {LoadingOutlined} from "@ant-design/icons";
import {DebouncedSearch, InputSizes, Text, TextAs, TextSizes} from "@clockout-dev/design-elements";
import styles from "../../styles/document-audit.module.scss";

/**
 * Member lookup: the reports page's `DebouncedSearch` + a compact result
 * list (name / email / client_id). Purely presentational — the page
 * controller owns the fetch, debounced result state and selection.
 *
 * @param {Object} props
 * @param {string} [props.query] — last searched term ("" = nothing searched yet)
 * @param {{clientId: string, name: string, email: string}[]} props.results
 * @param {boolean} [props.loading]
 * @param {string | null} [props.selectedClientId] — highlights the picked row
 * @param {boolean} [props.busy] — disables row picks (e.g. while a SOR loads)
 * @param {(term: string) => void} props.onSearch
 * @param {(member: {clientId: string, name: string, email: string}) => void} props.onSelect
 */
function MemberSearch({query = "", results = [], loading = false, selectedClientId = null, busy = false, onSearch, onSelect}) {
    return (
        <div className={styles.memberSearch}>
            <DebouncedSearch size={InputSizes.SMALL} placeholder="Search members by name, email, or ID..." onSearch={onSearch} debounceMs={300} classNames={{root: styles.searchInput}} aria-label="Search members" />

            {loading ? (
                <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.searchStatus}>
                    <LoadingOutlined className={styles.searchStatusIcon} />
                    Searching members…
                </Text>
            ) : null}

            {!loading && results.length ? (
                <div className={styles.resultList} role="listbox" aria-label="Member results">
                    {results.map(member => (
                        <button
                            key={member.clientId}
                            type="button"
                            role="option"
                            aria-selected={member.clientId === selectedClientId}
                            className={member.clientId === selectedClientId ? `${styles.resultRow} ${styles.resultRowSelected}` : styles.resultRow}
                            disabled={busy}
                            onClick={() => onSelect(member)}>
                            <span className={styles.resultName}>{member.name || "Unnamed member"}</span>
                            <span className={styles.resultEmail}>{member.email || "—"}</span>
                            <span className={styles.resultId}>{member.clientId}</span>
                        </button>
                    ))}
                </div>
            ) : null}

            {!loading && query && !results.length ? (
                <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.searchStatus}>
                    No members match "{query}" for this FI.
                </Text>
            ) : null}
        </div>
    );
}

export default MemberSearch;
